import {FunctionComponent, ReactNode, useCallback, useState} from 'react';
import {TabGroup, TabPanel, TabPanels} from '@headlessui/react';
import {useTranslation} from 'react-i18next';
import Arrow from '@/assets/images/icon-arrow-right.svg?react';
import Button from '@/components/button';
import PopupMenu from '@/components/popup-menu';
import Spinner from '@/components/spinner';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {useUserProfileContext} from '@/contexts/user-profile/use-user-profile-context';
import {useDocuments, useRemoveSentDocumentsHistory} from '@/hooks/queries/documents';
import {AdvisorRole} from '@/repositories/beneva-api/endpoints/users';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import ConfirmDialog from '../confirm-dialog';
import {useConfirmDialogActions} from '../confirm-dialog/use-confirm-dialog-actions';
import {NewDocumentCreationDialogButton} from '../new-document-creation';
import Header from './header';
import PanelIncomplete from './panels/incomplete';
import PanelPending from './panels/pending';
import PanelSent from './panels/sent';
import styles from './styles.module.scss';

interface ElementProps {
  isVisible: boolean;
  onClose: () => void;
  toggleRef?: React.RefObject<HTMLButtonElement | null>;
}

const DocumentMenu: FunctionComponent<ElementProps> = ({isVisible, onClose, toggleRef}) => {
  const {t} = useTranslation();
  const {profile} = useUserProfileContext();

  const [isDeleteHistoryConfirmDialogVisible, setIsDeleteHistoryConfirmDialogVisible] = useState(false);

  const {
    data: allDocuments,
    status,
    refetch,
  } = useDocuments({
    enabled: isVisible,
  });

  const removeSentDocumentsHistoryMutation = useRemoveSentDocumentsHistory();

  const canDeleteHistory = profile.advisorRole !== AdvisorRole.Assistant;

  const onShowDeleteHistoryConfirmDialog = useCallback(async () => {
    setIsDeleteHistoryConfirmDialogVisible(true);
  }, []);

  const onHideDeleteHistoryConfirmDialog = useCallback(() => {
    setIsDeleteHistoryConfirmDialogVisible(false);
  }, []);

  const onConfirmDeleteHistory = useCallback(async () => {
    await removeSentDocumentsHistoryMutation.mutateAsync();

    refetch();
    onHideDeleteHistoryConfirmDialog();
  }, [onHideDeleteHistoryConfirmDialog, refetch, removeSentDocumentsHistoryMutation]);

  const deleteHistoryConfirmDialogActions = useConfirmDialogActions({
    onConfirm: onConfirmDeleteHistory,
    onCancel: onHideDeleteHistoryConfirmDialog,
  });

  if (!isVisible) return;

  return (
    <>
      <PopupMenu className="-ml-2 -mt-4 px-0" onClose={onClose} toggleRef={toggleRef}>
        <TabGroup>
          <div className="px-6">
            <Header data-testid="menu_tabs-header" flags={allDocuments?.flags} />
          </div>

          <TabPanels>
            <LocalTabPanel data-testid="menu_tab_pending">
              {{
                content:
                  status === 'pending' ? (
                    <LocalTabPanelLoading />
                  ) : status === 'error' ? (
                    <LocalTabPanelError onRetry={refetch} />
                  ) : (
                    <PanelIncomplete
                      incompleteDocuments={allDocuments.incomplete}
                      pendingDocuments={allDocuments.complete}
                      closeMenu={onClose}
                    />
                  ),
                button: (
                  <NewDocumentCreationDialogButton data-testid="menu-new-document-button" onDialogClose={onClose} />
                ),
              }}
            </LocalTabPanel>

            <LocalTabPanel data-testid="menu_tab_pending">
              {{
                content:
                  status === 'pending' ? (
                    <LocalTabPanelLoading />
                  ) : status === 'error' ? (
                    <LocalTabPanelError onRetry={refetch} />
                  ) : (
                    <PanelPending
                      waitingSubmit={allDocuments.waitingSubmit}
                      waitingSignature={allDocuments.waitingSignature}
                      closeMenu={onClose}
                    />
                  ),
              }}
            </LocalTabPanel>

            <LocalTabPanel data-testid="menu_tab_sent">
              {{
                content:
                  status === 'pending' ? (
                    <LocalTabPanelLoading />
                  ) : status === 'error' ? (
                    <LocalTabPanelError onRetry={refetch} />
                  ) : (
                    <PanelSent documents={allDocuments.submitted} closeMenu={onClose} />
                  ),
                button: canDeleteHistory && (
                  <Button
                    className="w-full"
                    disabled={status === 'pending' || status === 'error' || allDocuments.submitted.length === 0}
                    label={t('components.document-menu.delete-history-list.button')}
                    trackingKey={WEB_CLICK_TRACKING_KEY.DELETE_SENT_DOCUMENT_HISTORY}
                    onClick={onShowDeleteHistoryConfirmDialog}
                    theme={BUTTON_THEME.SECONDARY}
                    data-testid="delete-history-list-button"
                  />
                ),
              }}
            </LocalTabPanel>
          </TabPanels>
        </TabGroup>
      </PopupMenu>

      {canDeleteHistory && (
        <ConfirmDialog
          isVisible={isDeleteHistoryConfirmDialogVisible}
          title={t('components.document-menu.delete-history-list.confirm-dialog.title')}
          text={t('components.document-menu.delete-history-list.confirm-dialog.text')}
          actions={deleteHistoryConfirmDialogActions}
        />
      )}
    </>
  );
};

type LocalTabPanelProps = Omit<React.ComponentProps<typeof TabPanel>, 'children'> & {
  children: {
    content: ReactNode;
    button?: ReactNode;
  };
};

const LocalTabPanel = ({children: {content, button}, ...props}: LocalTabPanelProps) => {
  return (
    <TabPanel className="flex flex-col" {...props}>
      <div className={`max-h-96 overflow-auto ${styles['shadowed-scrollview']}`}>{content}</div>
      {button && <div className="px-6 pt-5">{button} </div>}
    </TabPanel>
  );
};

const LocalTabPanelLoading = () => (
  <div data-testid="document-menu-loading-container" className="my-4 flex h-full justify-center">
    <Spinner />
  </div>
);

const LocalTabPanelError = ({onRetry}: {onRetry: () => void}) => {
  const {t} = useTranslation();

  return (
    <div
      data-testid="document-menu-error-container"
      className="flex h-36 w-full flex-col items-center justify-center px-6"
    >
      <div>{t('components.document-menu.document-list.errors.fetch.label')}</div>
      {onRetry && (
        <Button
          data-testid="document-menu-error-retry-button"
          theme={BUTTON_THEME.STYLELESS}
          variant={BUTTON_VARIANT.X_SMALL}
          icon={<Arrow className="size-4 fill-violet" />}
          label={t('components.document-menu.document-list.errors.fetch.cta')}
          trackingKey={WEB_CLICK_TRACKING_KEY.RETRY_FETCH_DOCUMENTS}
          onClick={onRetry}
        />
      )}
    </div>
  );
};

export default DocumentMenu;
